import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const SuccessModal = ({ isOpen, onClose, submittedData }) => {
  if (!isOpen) return null;

  const nextSteps = [
    {
      step: 1,
      title: "Confirmation Email",
      description: "You\'ll receive a confirmation email within 5 minutes",
      icon: "Mail",
      timeframe: "Within 5 minutes"
    },
    {
      step: 2,
      title: "Sales Team Review",
      description: "Our team will review your requirements and prepare a tailored demo",
      icon: "Users",
      timeframe: "Within 2 hours"
    },
    {
      step: 3,
      title: "Initial Contact",
      description: "A solutions engineer will reach out to schedule your demo",
      icon: "Phone",
      timeframe: "Within 2 business hours"
    },
    {
      step: 4,
      title: "Technical Demo",
      description: "Live demonstration of MessagingHub Pro tailored to your use case",
      icon: "Video",
      timeframe: "Within 24 hours"
    }
  ];

  const emergencyContact = {
    name: "Sarah Chen",
    role: "Enterprise Sales Director",
    phone: "+1 (555) 123-4567",
    email: "sarah.chen@messaginghub.pro"
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card rounded-xl border border-border shadow-elevation-4 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Success Header */}
          <div className="text-center mb-6">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-accent/10 rounded-full flex items-center justify-center">
                <Icon name="CheckCircle" size={32} className="text-accent" />
              </div>
            </div>
            <h2 className="text-2xl font-semibold text-foreground mb-2">
              Request Submitted Successfully!
            </h2>
            <p className="text-muted-foreground">
              Thank you {submittedData?.firstName}, we've received your request and will be in touch soon.
            </p>
          </div>

          {/* Submission Summary */}
          <div className="bg-muted/20 rounded-lg p-4 mb-6">
            <h3 className="text-sm font-medium text-foreground mb-3">Request Summary</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-muted-foreground">Company:</span>
                <span className="text-foreground ml-2">{submittedData?.company}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Use Case:</span>
                <span className="text-foreground ml-2">{submittedData?.useCase?.replace('-', ' ')}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Timeline:</span>
                <span className="text-foreground ml-2">{submittedData?.timeline?.replace('-', ' ')}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Volume:</span>
                <span className="text-foreground ml-2">{submittedData?.monthlyVolume}</span>
              </div>
            </div>
          </div>

          {/* Next Steps */}
          <div className="mb-6">
            <h3 className="text-lg font-medium text-foreground mb-4">What Happens Next</h3>
            <div className="space-y-4">
              {nextSteps?.map((step) => (
                <div key={step?.step} className="flex items-start space-x-4">
                  <div className="flex-shrink-0 w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center">
                    <span className="text-xs font-medium text-primary">{step?.step}</span>
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2 mb-1">
                      <Icon name={step?.icon} size={16} className="text-primary" />
                      <h4 className="text-sm font-medium text-foreground">{step?.title}</h4>
                    </div>
                    <p className="text-xs text-muted-foreground mb-1">{step?.description}</p>
                    <span className="inline-flex items-center px-2 py-1 rounded-full text-xs bg-accent/10 text-accent">
                      {step?.timeframe}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-6">
            <div className="flex items-start space-x-3">
              <Icon name="AlertCircle" size={20} className="text-warning flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-foreground mb-2">Need Immediate Assistance?</h4>
                <p className="text-xs text-muted-foreground mb-3">
                  If you have urgent questions or need to speak with someone immediately, contact our sales director directly:
                </p>
                <div className="space-y-1">
                  <div className="flex items-center space-x-2 text-xs">
                    <Icon name="User" size={12} className="text-warning" />
                    <span className="text-foreground font-medium">{emergencyContact?.name}</span>
                    <span className="text-muted-foreground">- {emergencyContact?.role}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <Icon name="Phone" size={12} className="text-warning" />
                    <span className="text-foreground">{emergencyContact?.phone}</span>
                  </div>
                  <div className="flex items-center space-x-2 text-xs">
                    <Icon name="Mail" size={12} className="text-warning" />
                    <span className="text-foreground">{emergencyContact?.email}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-6">
            <Button
              variant="outline"
              size="sm"
              iconName="FileText"
              iconPosition="left"
              onClick={() => window.open('/api-documentation', '_blank')}
            >
              View API Docs
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="DollarSign"
              iconPosition="left"
              onClick={() => window.open('/pricing-plans', '_blank')}
            >
              See Pricing
            </Button>
            <Button
              variant="outline"
              size="sm"
              iconName="Code"
              iconPosition="left"
              onClick={() => window.open('/developer-portal', '_blank')}
            >
              Developer Portal
            </Button>
          </div>

          {/* Reference Number */}
          <div className="text-center mb-6">
            <p className="text-xs text-muted-foreground">
              Reference Number: <span className="font-mono text-foreground">REQ-{Date.now()?.toString()?.slice(-8)}</span>
            </p>
          </div>

          {/* Close Button */}
          <div className="flex justify-center">
            <Button
              variant="default"
              onClick={onClose}
              iconName="X"
              iconPosition="right"
            >
              Close
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SuccessModal;